import { useState } from "react";
import { motion } from "framer-motion";
import { Calculator, ChevronDown, CheckCircle2, Send } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { TASFLOW_I18N } from "../i18n/tasflow";

function RoiCalculator() {
  const { lang } = useLang();
  const t = TASFLOW_I18N[lang].roi;
  const [f, setF] = useState({ requests: 500, minutes: 25, staff: 4, cost: 30000, rate: 60 });
  const set = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) || 0 });

  const manualHours = (f.requests * f.minutes) / 60;
  const afterHours = manualHours * (1 - f.rate / 100);
  const savedHours = Math.max(0, manualHours - afterHours);
  const costPerHour = f.staff > 0 ? f.cost / 176 : 0;
  const savedCost = Math.round(savedHours * costPerHour);
  const fmt = (n) => new Intl.NumberFormat(t.locale).format(Math.round(n));

  const FIELDS = [
    { k: "requests", label: t.fields.requests },
    { k: "minutes", label: t.fields.minutes },
    { k: "staff", label: t.fields.staff },
    { k: "cost", label: t.fields.cost },
    { k: "rate", label: t.fields.rate },
  ];

  return (
    <section className="py-24" data-testid="tasflow-roi">
      <div className="container-x">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#F97316]/40 bg-[#F97316]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FB923C]">
            <Calculator className="h-3 w-3" /> {t.badge}
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-white">{t.title}</h2>
        </div>
        <div className="mt-10 grid gap-8 lg:grid-cols-2 lg:items-center">
          <div className="space-y-4">
            {FIELDS.map((fl) => (
              <div key={fl.k}>
                <label className="text-sm text-white/60">{fl.label}</label>
                <input
                  type="number" min="0" value={f[fl.k]} onChange={set(fl.k)}
                  data-testid={`roi-${fl.k}`}
                  className="mt-1.5 w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white outline-none focus:border-[#F97316]/50 transition"
                />
              </div>
            ))}
          </div>
          <div className="rounded-3xl border border-[#F97316]/25 bg-gradient-to-br from-[#F97316]/10 to-white/[0.02] p-8">
            <div className="grid gap-5">
              <Stat label={t.stats.manual} value={`${fmt(manualHours)} ${t.hourUnit}`} />
              <Stat label={t.stats.after} value={`${fmt(afterHours)} ${t.hourUnit}`} />
              <Stat label={t.stats.saved} value={`${fmt(savedHours)} ${t.hourUnit}`} accent />
              <Stat label={t.stats.gain} value={`₺${fmt(savedCost)}`} accent />
            </div>
            <p className="mt-6 text-xs text-white/40">{t.note}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between border-b border-white/10 pb-3">
      <span className="text-sm text-white/60">{label}</span>
      <span className={`font-heading text-2xl font-extrabold ${accent ? "bg-gradient-to-r from-[#F97316] to-[#FB923C] bg-clip-text text-transparent" : "text-white"}`}>{value}</span>
    </div>
  );
}

function FaqSection() {
  const { lang } = useLang();
  const t = TASFLOW_I18N[lang].faq;
  const [open, setOpen] = useState(0);

  return (
    <section className="relative bg-[#020617] py-24" data-testid="tasflow-faq">
      <div className="absolute inset-0 bg-grid opacity-15" />
      <div className="container-x relative max-w-3xl">
        <span className="inline-flex items-center gap-2 rounded-full border border-[#F97316]/40 bg-[#F97316]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FB923C]">
          {t.badge}
        </span>
        <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-white">{t.title}</h2>
        <div className="mt-8 space-y-3">
          {t.items.map((item, i) => (
            <div key={item.q} className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`tasflow-faq-${i}`}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
              >
                <span className="text-sm sm:text-base font-semibold text-white">{item.q}</span>
                <ChevronDown className={`h-4 w-4 shrink-0 text-[#FB923C] transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <div className="px-5 pb-5 text-sm text-white/60 leading-relaxed">{item.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            inLanguage: lang,
            mainEntity: t.items.map((x) => ({
              "@type": "Question",
              name: x.q,
              acceptedAnswer: { "@type": "Answer", text: x.a },
            })),
          }),
        }}
      />
    </section>
  );
}

function DemoForm() {
  const { lang } = useLang();
  const t = TASFLOW_I18N[lang].demo;
  const [form, setForm] = useState({ name: "", company: "", role: "", phone: "", email: "", employee_count: "", monthly_requests: "", current_system: "", biggest_problem: "", interested_package: "Bilmiyorum", kvkk: false });
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.kvkk || !form.name || !form.company || !form.email) return;
    setStatus("loading");
    try {
      await api.post("/tas-flow/lead", { ...form, lang });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <section id="demo" className="py-24 scroll-mt-24" data-testid="tasflow-demo">
        <div className="container-x max-w-2xl text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-[#FB923C]" />
          <h2 className="mt-6 font-heading text-3xl font-bold text-white">{t.successTitle}</h2>
          <p className="mt-4 text-white/60">{t.successDesc}</p>
        </div>
      </section>
    );
  }

  const inputCls = "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-white placeholder:text-white/35 outline-none focus:border-[#F97316]/50 transition";
  const packageOptions = ["Starter", "Plus", "Pro", "Bilmiyorum"];

  return (
    <section id="demo" className="py-24 scroll-mt-24" data-testid="tasflow-demo">
      <div className="container-x max-w-3xl">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#F97316]/40 bg-[#F97316]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FB923C]">
            {t.badge}
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-bold text-white">{t.title}</h2>
          <p className="mt-3 text-white/60">{t.desc}</p>
        </div>
        <form onSubmit={submit} className="mt-10 grid gap-4 sm:grid-cols-2" data-testid="tasflow-demo-form">
          <input required placeholder={t.ph.name} value={form.name} onChange={set("name")} className={inputCls} data-testid="demo-name" />
          <input required placeholder={t.ph.company} value={form.company} onChange={set("company")} className={inputCls} data-testid="demo-company" />
          <input placeholder={t.ph.role} value={form.role} onChange={set("role")} className={inputCls} data-testid="demo-role" />
          <input placeholder={t.ph.phone} value={form.phone} onChange={set("phone")} className={inputCls} data-testid="demo-phone" />
          <input required type="email" placeholder={t.ph.email} value={form.email} onChange={set("email")} className={inputCls} data-testid="demo-email" />
          <input placeholder={t.ph.employees} value={form.employee_count} onChange={set("employee_count")} className={inputCls} data-testid="demo-employees" />
          <input placeholder={t.ph.requests} value={form.monthly_requests} onChange={set("monthly_requests")} className={inputCls} data-testid="demo-requests" />
          <input placeholder={t.ph.system} value={form.current_system} onChange={set("current_system")} className={inputCls} data-testid="demo-system" />
          <textarea placeholder={t.ph.problem} value={form.biggest_problem} onChange={set("biggest_problem")} rows={3} className={`${inputCls} sm:col-span-2`} data-testid="demo-problem" />
          <select value={form.interested_package} onChange={set("interested_package")} className={`${inputCls} sm:col-span-2`} data-testid="demo-package">
            {packageOptions.map((p) => (
              <option key={p} value={p}>
                {`${t.packagePrefix} ${p === "Bilmiyorum" ? t.unknown : p}`}
              </option>
            ))}
          </select>
          <label className="flex items-start gap-2.5 sm:col-span-2 text-sm text-white/60">
            <input type="checkbox" checked={form.kvkk} onChange={set("kvkk")} className="mt-1 accent-[#F97316]" data-testid="demo-kvkk" required />
            <span>{t.kvkk}</span>
          </label>
          {status === "error" && <p className="sm:col-span-2 text-sm text-red-400">{t.error}</p>}
          <button
            type="submit" disabled={status === "loading"}
            data-testid="demo-submit"
            className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#F97316] to-[#FB923C] px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#F97316]/25 transition hover:-translate-y-0.5 disabled:opacity-60"
          >
            {status === "loading" ? t.sending : <>{t.submit} <Send className="h-4 w-4" /></>}
          </button>
        </form>
      </div>
    </section>
  );
}

export default function TasFlowFaz3() {
  return (
    <>
      <RoiCalculator />
      <FaqSection />
      <DemoForm />
    </>
  );
}
